import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, Gavel, CheckCircle2, XCircle, ChevronLeft, Ban } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_CLS = { open: "text-nc-blue border-nc-blue/40 bg-nc-blue/10", answered: "text-nc-green border-nc-green/40 bg-nc-green/10", closed: "text-zinc-500 border-white/15 bg-white/5" };
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

export default function AdminSupport() {
  const [tab, setTab] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/support/admin/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const appeals = tickets.filter((t) => t.category === "ban-appeal" && t.status !== "closed");
  const shown = tab === "appeals" ? appeals : tickets.filter((t) => tab === "all" || t.status === tab);

  const openTicket = async (id) => {
    const r = await api.get(`/support/tickets/${id}`);
    setOpen(r.data);
  };

  const act = async (fn, ok) => {
    setSending(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
      if (open) openTicket(open.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendReply = () => act(() => api.post(`/support/tickets/${open.id}/reply`, { message: reply }).then(() => setReply("")), "Reply sent");
  const setStatus = (status) => act(() => api.patch(`/support/admin/tickets/${open.id}`, { status }), `Ticket ${status}`);
  const unbanUser = (userId) => act(() => api.post(`/users/${userId}/unban`), "Account unbanned");

  return (
    <section data-testid="admin-support-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <LifeBuoy className="w-4 h-4 text-nc-blue" /> Support Desk
        </h2>
        <div className="flex gap-2 flex-wrap">
          {[["open", `Open (${tickets.filter((t) => t.status === "open" && t.category !== "ban-appeal").length})`],
            ["answered", "Answered"],
            ["all", "All"],
            ["appeals", `Ban Appeals (${appeals.length})`]].map(([t, label]) => (
            <button
              key={t}
              data-testid={`admin-support-tab-${t}`}
              onClick={() => { setTab(t); setOpen(null); }}
              className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors duration-300 ${
                tab === t ? (t === "appeals" ? "text-nc-orange border-nc-orange/60 bg-nc-orange/10" : "text-nc-blue border-nc-blue/60 bg-nc-blue/10") : "text-zinc-400 border-white/10 hover:border-white/30"
              }`}
            >
              {t === "appeals" && <Gavel className="w-3 h-3 inline mr-1" />}{label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {open ? (
          <div className="flex flex-col gap-4">
            <button data-testid="admin-support-back" onClick={() => setOpen(null)} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-100 flex items-center gap-2 transition-colors">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to tickets
            </button>
            <div className="border border-white/10 p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap border-b border-white/10 pb-4 mb-4">
                <div>
                  <p className="font-sans font-bold text-base text-zinc-100">{open.subject}</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">
                    {open.user_name} // {open.user_email} // {open.category} // {String(open.createdAt).slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_CLS[open.status]}`}>{open.status}</span>
                  {open.status !== "closed" && (
                    <button data-testid="admin-support-close" onClick={() => setStatus("closed")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-white/20 text-zinc-400 hover:border-white/40 transition-colors flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Close
                    </button>
                  )}
                  {open.category === "ban-appeal" && (
                    <button data-testid="admin-support-unban" onClick={() => unbanUser(open.user_id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-nc-green/40 text-nc-green hover:bg-nc-green/10 transition-colors flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Unban User
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {open.messages.map((m, i) => (
                  <div key={i} className={`border p-4 ${m.role === "admin" ? "border-nc-orange/30 bg-nc-orange/5 ml-6" : "border-white/10 bg-[#050505]/60 mr-6"}`}>
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase mb-1.5">
                      <span className={m.role === "admin" ? "text-nc-orange" : "text-nc-blue"}>{m.role === "admin" ? "Support (you)" : m.from}</span>
                      <span className="text-zinc-600"> // {String(m.at).slice(0, 16).replace("T", " ")}</span>
                    </p>
                    <p className="font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
              {open.status !== "closed" && (
                <div className="flex gap-2 mt-4 border-t border-white/10 pt-4">
                  <input data-testid="admin-support-reply-input" className={inputCls} placeholder="Reply to user…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && reply.trim()) sendReply(); }} />
                  <button data-testid="admin-support-reply-button" onClick={sendReply} disabled={sending || !reply.trim()} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-5 flex items-center transition-colors">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {shown.length === 0 && <p data-testid="admin-support-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">Nothing in this queue.</p>}
            {shown.map((t, i) => (
              <button
                key={t.id}
                data-testid={`admin-support-ticket-${i + 1}`}
                onClick={() => openTicket(t.id)}
                className={`text-left border p-4 transition-colors duration-300 ${t.category === "ban-appeal" ? "border-red-500/30 bg-red-500/5 hover:border-red-500/60" : "border-white/10 bg-[#050505]/50 hover:border-nc-blue/50"}`}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    {t.category === "ban-appeal" ? <Ban className="w-4 h-4 text-red-400 shrink-0" /> : <LifeBuoy className="w-4 h-4 text-nc-blue shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{t.subject}</p>
                      <p className="font-mono text-[10px] text-zinc-500">{t.user_name} // {t.category} // {t.messages.length} msg</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${STATUS_CLS[t.status]}`}>{t.status}</span>
                    <span className="font-mono text-[10px] text-zinc-600">{String(t.updatedAt).slice(0, 10)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
